/**
 * Análises › Explorador (D522; doc 20 §4.3): the explorer's state and the rules around it — which measures and cuts are OFFERED
 * (the catalog is the authority; a person's name only with `analytics:view_personal`; distinct-people measures never beside an
 * entity-level cut without it), how a state becomes a request, how a click on a row becomes a drill-down request (a NEW server
 * call; the client never filters rows it already has), the trail back, and the ranking (top N by any measure).
 *
 * The state is plain data (it is what a saved report stores — the QUERY, never an answer) and is validated when read back.
 */
import {
  DIMENSIONS,
  getDimension,
  type CompareAgainst,
  type DrillTarget,
  type Granularity,
  type QueryFilter,
  type QueryRequest,
  type ResultRow,
  drillDown,
} from '@vello/analytics-semantic';
import { type AnalyticsBiCatalogView, type AnalyticsBiQueryView } from '@vello/api-client';
import { type BiCompareChoice, type BiPeriod, BI_ALL_PRESETS, BI_COMPARE_CHOICES, DEFAULT_PERIOD, compareFields, customRangeError, lastDaysValid, periodRequest } from './bi-core.js';
import { type BiFilterSpec, type BiHavingSpec, activeFilterState, filterRequestParts, isFilled } from './bi-filters.js';
import { FILTER_OPS, HAVING_OPS } from '@vello/analytics-semantic';

export type { BiFilterSpec, BiHavingSpec };

export type ExplorerMode = 'table' | 'ranking';
export const PAGE_SIZES = [10, 25, 50, 100] as const;
export const RANKING_SIZES = [5, 10, 20, 50] as const;
/** The grains the explorer offers for time; the finer ones are reached by drilling. */
export const GRAINS: readonly Granularity[] = ['total', 'year', 'month', 'day', 'hour'];

export interface ExplorerState {
  readonly mode: ExplorerMode;
  readonly metrics: readonly string[];
  readonly dimensions: readonly string[];
  readonly grain: Granularity;
  readonly filters: readonly BiFilterSpec[];
  /** D528: OR groups of conditions (one level) besides `filters`. */
  readonly anyOf: readonly (readonly BiFilterSpec[])[];
  /** D528: conditions on the value of a selected measure, after the aggregation. */
  readonly having: readonly BiHavingSpec[];
  readonly period: BiPeriod;
  readonly compare: BiCompareChoice;
  readonly upToNowAgainst: CompareAgainst;
  /** A metric or dimension id; absent = the first metric. */
  readonly sortBy?: string;
  readonly sortDir: 'asc' | 'desc';
  readonly pageSize: number;
  readonly page: number;
}

export function defaultExplorerState(): ExplorerState {
  return {
    mode: 'table',
    metrics: ['orders'],
    dimensions: ['market'],
    grain: 'total',
    filters: [],
    anyOf: [],
    having: [],
    period: DEFAULT_PERIOD,
    compare: 'auto',
    upToNowAgainst: 'last_year_by_date',
    sortDir: 'desc',
    pageSize: 25,
    page: 0,
  };
}

/** The ranking preset: one measure cut by one dimension, the top (or bottom) N, no paging. */
export function rankingState(base: ExplorerState, input: { readonly metric: string; readonly dimension: string; readonly dir: 'asc' | 'desc'; readonly size: number }): ExplorerState {
  return { ...base, mode: 'ranking', metrics: [input.metric], dimensions: [input.dimension], grain: 'total', sortBy: input.metric, sortDir: input.dir, pageSize: input.size, page: 0 };
}

// ───────────────────────────── what is offered ─────────────────────────────

export interface BiAccess {
  /** `analytics:view_personal` — names, and the person/order grain. */
  readonly personal: boolean;
}

const TIME_GRAIN_DIMS = new Set(['year', 'month', 'day', 'hour']);

type CatalogDimension = AnalyticsBiCatalogView['dimensions'][number];

/**
 * Why a choice cannot be made right now. The first three are the SERVER'S reason codes, read from the catalog (`metrics[].refused`, D523):
 * the screen never works out for itself who may see a person or which measures can share a table. The last is the star's own availability
 * (`metrics[].dimensions`, the cuts a measure has at all).
 */
export type OfferRefusal = 'ANALYTICS_PERSONAL_FORBIDDEN' | 'ANALYTICS_LINKAGE_FORBIDDEN' | 'METRICS_SPAN_FACT_TABLES' | 'DIMENSION_NOT_AVAILABLE_FOR_METRICS';

export interface Offer {
  readonly id: string;
  /** Present when the choice is shown but disabled, with the reason and the choice that causes it (a measure or a cut). */
  readonly refusal?: { readonly code: OfferRefusal; readonly because?: string };
}

const DIMENSION_ORDER: ReadonlyMap<string, number> = new Map(DIMENSIONS.map((d, i) => [d.id, i]));
const byCatalogOrder = (a: string, b: string): number => (DIMENSION_ORDER.get(a) ?? 999) - (DIMENSION_ORDER.get(b) ?? 999);

/** A dimension's facts, even for one the catalog does not list to this caller (a person without the word): the star's own definition. */
export function dimensionInfo(catalog: AnalyticsBiCatalogView, id: string): Pick<CatalogDimension, 'id' | 'personal' | 'valueType'> {
  const listed = catalog.dimensions.find((d) => d.id === id);
  if (listed !== undefined) return listed;
  const own = getDimension(id);
  return own !== undefined ? { id, personal: own.personal, valueType: own.valueType } : { id, personal: false, valueType: 'ref' };
}

/**
 * The cuts the picker lists for the chosen measures, in the catalog's order, each enabled or disabled with the reason: what the
 * caller's words refuse beside a chosen measure (the server's `refused.dimensions`), and what a chosen measure cannot be cut by at all.
 * The time grains are a separate control and are not listed.
 */
export function dimensionOffers(catalog: AnalyticsBiCatalogView, metrics: readonly string[]): readonly Offer[] {
  // A record-level number (an order's own distance...) is filtered on, never grouped by: it is not a cut.
  const filterOnly = new Set(catalog.dimensions.filter((d) => d.filterOnly).map((d) => d.id));
  return offersFor(catalog, metrics, (id) => !TIME_GRAIN_DIMS.has(id) && !filterOnly.has(id));
}

/**
 * D528: the dimensions a CONDITION may be about beside the chosen measures, each enabled or disabled with the server's own reason code:
 * every dimension of the catalog (the time grains and the record-level numbers included), a person for a caller without the word shown
 * disabled with `ANALYTICS_PERSONAL_FORBIDDEN`, and one the chosen measures have no table for `DIMENSION_NOT_AVAILABLE_FOR_METRICS`.
 */
export function filterOffers(catalog: AnalyticsBiCatalogView, metrics: readonly string[]): readonly Offer[] {
  return offersFor(catalog, metrics, () => true);
}

function offersFor(catalog: AnalyticsBiCatalogView, metrics: readonly string[], keep: (id: string) => boolean): readonly Offer[] {
  const chosen = catalog.metrics.filter((m) => metrics.includes(m.id));
  const listed = catalog.dimensions.map((d) => d.id).filter(keep);
  const refusedIds = chosen.flatMap((m) => m.refused.dimensions.map((r) => r.id)).filter(keep);
  return [...new Set([...listed, ...refusedIds])].sort(byCatalogOrder).map((id): Offer => {
    // The most specific reason wins when several chosen measures refuse it: the linkage rule (a distinct-people count) over the plain one.
    const refusals = chosen.flatMap((m) => m.refused.dimensions.filter((r) => r.id === id).map((r) => ({ code: r.code as OfferRefusal, because: m.id })));
    const refused = refusals.find((r) => r.code === 'ANALYTICS_LINKAGE_FORBIDDEN') ?? refusals[0];
    if (refused !== undefined) return { id, refusal: refused };
    const lacking = chosen.find((m) => !m.dimensions.includes(id));
    return lacking !== undefined ? { id, refusal: { code: 'DIMENSION_NOT_AVAILABLE_FOR_METRICS', because: lacking.id } } : { id };
  });
}

/**
 * The measures the picker lists, each enabled or disabled beside what is already chosen: another measure no single table carries with
 * it (`METRICS_SPAN_FACT_TABLES`), or a chosen cut the caller's words refuse beside it (a person or an order). A measure that merely lacks
 * a chosen cut is NOT disabled: choosing it takes that cut out (`dimensionOffers` already says why the cut is unavailable), so changing
 * measures never leads to a state the screen must dead-end on. `cuts` are the chosen dimension ids, the time grain included.
 */
export function metricOffers(catalog: AnalyticsBiCatalogView, metrics: readonly string[], cuts: readonly string[]): readonly Offer[] {
  const chosen = catalog.metrics.filter((m) => metrics.includes(m.id));
  return catalog.metrics.map((m): Offer => {
    if (metrics.includes(m.id)) return { id: m.id };
    const spanning = chosen.find((c) => c.refused.metrics.some((r) => r.id === m.id));
    if (spanning !== undefined) return { id: m.id, refusal: { code: 'METRICS_SPAN_FACT_TABLES', because: spanning.id } };
    for (const cut of cuts) {
      const refused = m.refused.dimensions.find((r) => r.id === cut);
      if (refused !== undefined) return { id: m.id, refusal: { code: refused.code as OfferRefusal, because: cut } };
    }
    return { id: m.id };
  });
}

/** The ids of the offers that can be picked now. */
export function enabledIds(offers: readonly Offer[]): ReadonlySet<string> {
  return new Set(offers.filter((o) => o.refusal === undefined).map((o) => o.id));
}

/** The state with every choice the catalog does not offer taken out (a saved report opened by someone with less access). */
export function sanitizeState(state: ExplorerState, catalog: AnalyticsBiCatalogView): ExplorerState {
  const known = new Set(catalog.dimensions.map((d) => d.id));
  const metrics = state.metrics.filter((id) => catalog.metrics.some((m) => m.id === id));
  const dimensions = state.dimensions.filter((id) => known.has(id));
  const filters = state.filters.filter((f) => known.has(f.dimension));
  const anyOf = state.anyOf.map((g) => g.filter((f) => known.has(f.dimension))).filter((g) => g.length > 0);
  const having = state.having.filter((h) => metrics.includes(h.metric));
  return { ...state, metrics, dimensions, filters, anyOf, having };
}

// ───────────────────────────── state → request ─────────────────────────────

/** Why a state cannot be asked yet (the screen says it in words next to the control). */
export type ExplorerIssue = 'metrics' | 'period_incomplete' | 'period_invalid' | 'period_reversed';

export function explorerIssue(state: ExplorerState): ExplorerIssue | undefined {
  if (state.metrics.length === 0) return 'metrics';
  if (state.period.kind === 'lastDays' && !lastDaysValid(state.period.days)) return 'period_invalid';
  if (state.period.kind === 'custom') {
    const e = customRangeError(state.period.from, state.period.to);
    if (e === 'incomplete') return 'period_incomplete';
    if (e === 'invalid') return 'period_invalid';
    if (e === 'reversed') return 'period_reversed';
  }
  return undefined;
}

/** The filters with what their operator needs. A filter the operator has not filled yet is not sent. */
export function activeFilters(filters: readonly BiFilterSpec[]): readonly BiFilterSpec[] {
  return filters.filter((f) => isFilled(f));
}

/**
 * The request of a state. `scope` (the operator's switcher) comes first; a user filter on the same dimension replaces it, so the
 * explorer can narrow within the scope but the server's reach is what finally bounds it.
 */
export function explorerRequest(state: ExplorerState, scope: readonly QueryFilter[] = [], options: { readonly fallback?: boolean; readonly page?: boolean } = {}): QueryRequest {
  const mine = activeFilterState({ filters: state.filters, anyOf: state.anyOf, having: state.having.filter((h) => state.metrics.includes(h.metric)) });
  // The scope filter of the switcher is replaced by the operator's own condition on the same dimension (a market or a country): they can
  // narrow within the scope, and the server's reach is what finally bounds it.
  const named = new Set([...mine.filters, ...mine.anyOf.flat()].map((f) => f.dimension));
  const parts = filterRequestParts(mine);
  const filters: QueryFilter[] = [...scope.filter((s) => !named.has(s.dimension)).map((f) => ({ ...f, values: [...f.values] })), ...(parts.filters ?? [])];
  const sortBy = state.sortBy ?? state.metrics[0];
  const paged = options.page !== false;
  return {
    metrics: [...state.metrics],
    ...(state.dimensions.length > 0 ? { dimensions: [...state.dimensions] } : {}),
    ...(filters.length > 0 ? { filters } : {}),
    ...(parts.anyOf !== undefined ? { anyOf: parts.anyOf } : {}),
    ...(parts.having !== undefined ? { having: parts.having } : {}),
    period: periodRequest(state.period),
    ...(state.grain !== 'total' ? { granularity: state.grain } : {}),
    ...compareFields(state.compare, state.upToNowAgainst, options.fallback === true),
    ...(sortBy !== undefined && (state.dimensions.length > 0 || state.grain !== 'total') ? { sort: [{ by: sortBy, direction: state.sortDir }] } : {}),
    limit: state.pageSize,
    ...(paged && state.mode === 'table' && state.page > 0 ? { offset: state.page * state.pageSize } : {}),
  };
}

/** The same query without paging and with the server's cap, for the CSV export (the export route pages nothing). */
export function exportRequest(state: ExplorerState, scope: readonly QueryFilter[] = [], options: { readonly fallback?: boolean } = {}): QueryRequest {
  const { limit: _limit, offset: _offset, ...rest } = explorerRequest(state, scope, { ...options, page: false });
  return rest;
}

// ───────────────────────────── drill-down ─────────────────────────────

export interface DrillChoice {
  readonly target: DrillTarget;
  /** The dimension the click opens (the child on the catalog's hierarchy). */
  readonly into: string;
}

/**
 * The ways a row of this answer can be opened one level deeper: one per cut of the answer that has a visible child on the catalog's
 * hierarchy (in the order of the cuts, time first). One choice drills at once; several ask "drill by" (D523).
 */
export function drillChoices(catalog: AnalyticsBiCatalogView, view: Pick<AnalyticsBiQueryView, 'dimensions'>, row: ResultRow): readonly DrillChoice[] {
  const out: DrillChoice[] = [];
  for (const used of view.dimensions) {
    const dim = catalog.dimensions.find((d) => d.id === used.id);
    const child = dim?.hierarchy?.child;
    if (dim === undefined || child === undefined) continue;
    if (!catalog.dimensions.some((d) => d.id === child)) continue; // a child the caller may not see is not offered
    const key = row.keys[used.id]?.key;
    if (key === null || key === undefined) continue;
    out.push({ target: { dimension: used.id, key, ...(row.marketId !== undefined ? { marketId: row.marketId } : {}) }, into: child });
  }
  return out;
}

/**
 * The state one level deeper. The drill is computed on the request the answer was ASKED with, its relative period replaced by
 * the window the server resolved (so a drill can only narrow it), and the result is a new state: the screen then asks the
 * server again — no row is filtered on the client.
 */
export function drillState(state: ExplorerState, view: Pick<AnalyticsBiQueryView, 'period'>, target: DrillTarget): { readonly state: ExplorerState } | { readonly code: string } {
  const asked: QueryRequest = { ...explorerRequest(state, [], { page: false }), period: { from: view.period.from, to: view.period.to } };
  const drilled = drillDown(asked, target);
  if (!drilled.ok) return { code: drilled.code };
  const req = drilled.value;
  const period = 'relative' in req.period ? state.period : { kind: 'custom' as const, from: req.period.from, to: req.period.to };
  const scopeless = (req.filters ?? []).map((f): BiFilterSpec => ({ dimension: f.dimension, op: f.op ?? 'in', values: [...f.values], ...(f.currency !== undefined ? { currency: f.currency } : {}) }));
  return {
    state: {
      ...state,
      mode: 'table',
      dimensions: req.dimensions ?? [],
      grain: req.granularity ?? 'total',
      filters: scopeless,
      period,
      page: 0,
    },
  };
}

export interface TrailStep {
  readonly label: string;
  /** The state to restore when the step is clicked (the one BEFORE the drill). */
  readonly state: ExplorerState;
}

/** Click on a breadcrumb: restore its state and drop it and everything after it. */
export function trailBack(trail: readonly TrailStep[], index: number): { readonly state: ExplorerState; readonly trail: readonly TrailStep[] } | undefined {
  const step = trail[index];
  return step === undefined ? undefined : { state: step.state, trail: trail.slice(0, index) };
}

// ───────────────────────────── reading a state back ─────────────────────────────

const ID = /^[a-z][a-z0-9_]{0,63}$/;
const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const ids = (v: unknown, max: number): string[] | undefined => (Array.isArray(v) && v.length <= max && v.every((x) => typeof x === 'string' && ID.test(x)) ? (v as string[]) : undefined);

/** Validate an untrusted state (a stored report): anything unknown or malformed makes the whole report unreadable, never half-applied. */
export function parseExplorerState(raw: unknown): ExplorerState | undefined {
  if (!isRecord(raw)) return undefined;
  const metrics = ids(raw.metrics, 12);
  const dimensions = ids(raw.dimensions, 4);
  if (metrics === undefined || dimensions === undefined) return undefined;
  const grain = raw.grain;
  if (typeof grain !== 'string' || !GRAINS.includes(grain as Granularity)) return undefined;
  const mode = raw.mode === 'ranking' ? 'ranking' : raw.mode === 'table' ? 'table' : undefined;
  if (mode === undefined) return undefined;
  const parseSpec = (f: unknown): BiFilterSpec | undefined => {
    if (!isRecord(f) || typeof f.dimension !== 'string' || !ID.test(f.dimension)) return undefined;
    if (typeof f.op !== 'string' || !(FILTER_OPS as readonly string[]).includes(f.op)) return undefined;
    if (!Array.isArray(f.values) || f.values.length > 100 || !f.values.every((v) => typeof v === 'string' && v.length <= 160)) return undefined;
    if (f.currency !== undefined && (typeof f.currency !== 'string' || !/^[A-Z]{3}$/.test(f.currency))) return undefined;
    return { dimension: f.dimension, op: f.op as BiFilterSpec['op'], values: f.values as string[], ...(f.currency !== undefined ? { currency: f.currency as string } : {}) };
  };
  if (!Array.isArray(raw.filters) || raw.filters.length > 16) return undefined;
  const filters: BiFilterSpec[] = [];
  for (const f of raw.filters as unknown[]) {
    const spec = parseSpec(f);
    if (spec === undefined) return undefined;
    filters.push(spec);
  }
  // D528: a state saved before the OR groups and HAVING existed has neither.
  const groupsRaw = raw.anyOf ?? [];
  if (!Array.isArray(groupsRaw) || groupsRaw.length > 8) return undefined;
  const anyOf: BiFilterSpec[][] = [];
  for (const g of groupsRaw as unknown[]) {
    if (!Array.isArray(g) || g.length > 16) return undefined;
    const group: BiFilterSpec[] = [];
    for (const f of g as unknown[]) {
      const spec = parseSpec(f);
      if (spec === undefined) return undefined;
      group.push(spec);
    }
    anyOf.push(group);
  }
  const havingRaw = raw.having ?? [];
  if (!Array.isArray(havingRaw) || havingRaw.length > 4) return undefined;
  const having: BiHavingSpec[] = [];
  for (const h of havingRaw as unknown[]) {
    if (!isRecord(h) || typeof h.metric !== 'string' || !ID.test(h.metric) || typeof h.op !== 'string' || !(HAVING_OPS as readonly string[]).includes(h.op)) return undefined;
    if (!Array.isArray(h.values) || h.values.length > 2 || !h.values.every((v) => typeof v === 'string' && v.length <= 40)) return undefined;
    if (h.currency !== undefined && (typeof h.currency !== 'string' || !/^[A-Z]{3}$/.test(h.currency))) return undefined;
    having.push({ metric: h.metric, op: h.op as BiHavingSpec['op'], values: h.values as string[], ...(h.currency !== undefined ? { currency: h.currency as string } : {}) });
  }
  const p = raw.period;
  let period: BiPeriod;
  if (isRecord(p) && p.kind === 'preset' && typeof p.preset === 'string' && (BI_ALL_PRESETS as readonly string[]).includes(p.preset)) period = { kind: 'preset', preset: p.preset as (typeof BI_ALL_PRESETS)[number] };
  else if (isRecord(p) && p.kind === 'lastDays' && typeof p.days === 'number' && lastDaysValid(p.days)) period = { kind: 'lastDays', days: p.days };
  else if (isRecord(p) && p.kind === 'custom' && typeof p.from === 'string' && typeof p.to === 'string') period = { kind: 'custom', from: p.from, to: p.to };
  else return undefined;
  if (typeof raw.compare !== 'string' || !BI_COMPARE_CHOICES.includes(raw.compare as BiCompareChoice)) return undefined;
  const against = raw.upToNowAgainst;
  if (against !== 'previous_period' && against !== 'last_year_by_date' && against !== 'last_year_by_weekday') return undefined;
  if (raw.sortBy !== undefined && (typeof raw.sortBy !== 'string' || !ID.test(raw.sortBy))) return undefined;
  if (raw.sortDir !== 'asc' && raw.sortDir !== 'desc') return undefined;
  const size = raw.pageSize;
  if (typeof size !== 'number' || !Number.isInteger(size) || size < 1 || size > 1000) return undefined;
  const page = raw.page;
  if (typeof page !== 'number' || !Number.isInteger(page) || page < 0 || page > 1000) return undefined;
  return {
    mode,
    metrics,
    dimensions,
    grain: grain as Granularity,
    filters,
    anyOf,
    having,
    period,
    compare: raw.compare as BiCompareChoice,
    upToNowAgainst: against,
    ...(raw.sortBy !== undefined ? { sortBy: raw.sortBy as string } : {}),
    sortDir: raw.sortDir,
    pageSize: size,
    page,
  };
}
